// Kritik daire taraması: merkez ızgarası × yarıçap dizisi, ardından en iyi dairenin çevresinde daralan yerel iyileştirme.
import type { SlipCircle, SlopeMethod } from "../types/result";
import { factorOfSafety } from "./bishop";
import type { GroundModel } from "./model";
import { allocSlices, buildSlices, type SliceArrays } from "./slices";

export interface SearchOptions {
  method: SlopeMethod; kh: number; slices: number;
  xRange: [number, number]; yRange: [number, number]; step: number;
  radiusStep: number;
  /** Merkez için yarıçap aralığı; rMax < rMin ise merkez atlanır */
  radii: (cx: number, cy: number) => [number, number];
  /** Yerel iyileştirme turu (varsayılan 3; 0 = yok) */
  refine?: number;
}
export interface SearchBest { fs: number; circle: SlipCircle; searched: number }

const MAX_RADII = 40;

export function gridCounts(o: Pick<SearchOptions, "xRange" | "yRange" | "step">): { nx: number; ny: number } {
  return { nx: Math.max(1, Math.floor((o.xRange[1] - o.xRange[0]) / o.step + 1e-9) + 1), ny: Math.max(1, Math.floor((o.yRange[1] - o.yRange[0]) / o.step + 1e-9) + 1) };
}

/** Tek daire: dilimler geçersizse null */
export function circleFs(model: GroundModel, circle: SlipCircle, method: SlopeMethod, kh: number, arr: SliceArrays): number | null {
  if (!buildSlices(model, circle, arr)) return null;
  const fs = factorOfSafety(arr, { method, kh, cy: circle.cy, r: circle.r }).fs;
  return Number.isFinite(fs) && fs > 0 ? fs : null;
}

export function searchCritical(model: GroundModel, o: SearchOptions): SearchBest | null {
  const arr = allocSlices(o.slices);
  const { nx, ny } = gridCounts(o);
  let best: SearchBest | null = null;
  let searched = 0;
  const tryCircle = (cx: number, cy: number, r: number): void => {
    const fs = circleFs(model, { cx, cy, r }, o.method, o.kh, arr);
    if (fs === null) return;
    searched++;
    if (!best || fs < best.fs) best = { fs, circle: { cx, cy, r }, searched: 0 };
  };
  for (let i = 0; i < nx; i++) {
    const cx = o.xRange[0] + i * o.step;
    for (let j = 0; j < ny; j++) {
      const cy = o.yRange[0] + j * o.step;
      const [rMin, rMax] = o.radii(cx, cy);
      if (!(rMax >= rMin) || rMin <= 0) continue;
      const nR = Math.min(MAX_RADII, Math.floor((rMax - rMin) / o.radiusStep + 1e-9) + 1);
      for (let k = 0; k < nR; k++) tryCircle(cx, cy, rMin + k * o.radiusStep);
    }
  }
  if (!best) return null;
  // Yerel iyileştirme: 3×3×3 komşuluk, adım her turda yarıya
  let dx = o.step / 2, dr = o.radiusStep / 2;
  for (let round = 0; round < (o.refine ?? 3); round++) {
    for (let pass = 0; pass < 3; pass++) {
      const c: SlipCircle = (best as SearchBest).circle;
      for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
        if (i === 0 && j === 0 && k === 0) continue;
        const r = c.r + k * dr, cx = c.cx + i * dx, cy = c.cy + j * dx;
        if (r <= 0 || r < o.radii(cx, cy)[0]) continue;     // en az derinlik kuralı iyileştirmede de geçerli
        tryCircle(cx, cy, r);
      }
    }
    dx /= 2; dr /= 2;
  }
  (best as SearchBest).searched = searched;
  return best;
}
